import { createEffect, createSignal, For, Show, type JSX } from "solid-js";
import { VsChromeClose } from "solid-icons/vs";
import { useI18n } from "./i18n";

export const [modalVisible, setModalVisible] = createSignal(false);
const [modalTitle, setModalTitle] = createSignal("");
const [modalBody, setModalBody] = createSignal<JSX.Element>("");
const [modalButtonValidate, setModalButtonValidate] = createSignal<string | null>(null);
const [modalButtonCancel, setModalButtonCancel] = createSignal<string | null>(null);

export const showDialog = (body: string, title: string) => {
  setModalVisible(true);
  setModalTitle(title);
  setModalBody(body);
  setModalButtonValidate("OK");
  setModalButtonCancel(null);
};

const [fakeModalClicked, setFakeModalClicked] = createSignal(false);
export const showFakeBookSearchDialog = () => {
  setModalVisible(true);
  setModalTitle("");
  setModalBody(
    <div class="w-full">
      <div class="flex justify-between bg-gray-300 p-4">
        <For
          each={[
            "A",
            "B",
            "C",
            "D",
            "E",
            "F",
            "G",
            "H",
            "I",
            "J",
            "K",
            "L",
            "M",
            "N",
            "O",
            "P",
            "Q",
            "R",
            "S",
            "T",
            "U",
            "V",
            "W",
            "X",
            "Y",
            "Z",
          ]}
        >
          {(letter) => (
            <div
              onClick={() => setFakeModalClicked(false)}
              class="flex h-6 w-6 items-center justify-center border border-gray-800 bg-gray-400 text-white hover:cursor-pointer hover:bg-gray-600"
            >
              {letter}
            </div>
          )}
        </For>
      </div>
      <Show when={!fakeModalClicked()}>
        <img
          class="hover:cursor-pointer"
          src="/assets/Interface_Livre_1.jpg"
          alt="interface de recherche"
          height="600"
          onClick={() => setFakeModalClicked(true)}
        />
      </Show>
      <Show when={fakeModalClicked()}>
        <img
          class="hover:cursor-pointer"
          src="/assets/Interface_Livre_2.jpg"
          alt="interface de recherche"
          height="600"
          onClick={() => setFakeModalClicked(false)}
        />
      </Show>
    </div>
  );
  setModalButtonValidate(null);
  setModalButtonCancel(null);
};

export const SimpleDialogViaAPI = () => {
  return (
    <Show when={modalVisible()}>
      <SimpleDialog
        isOpen={modalVisible()}
        onClose={() => setModalVisible(false)}
        onValidate={() => setModalVisible(false)}
        title={modalTitle()}
        buttonValidate={modalButtonValidate()}
        buttonCancel={modalButtonCancel()}
      >
        {modalBody()}
      </SimpleDialog>
    </Show>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // Return false to keep the dialog open (e.g. on validation failure);
  // void, undefined, a Promise, or any other value lets the dialog close
  // as usual.
  onValidate?: () => unknown;
  title: JSX.Element;
  children: JSX.Element;
  buttonValidate?: JSX.Element;
  buttonCancel?: JSX.Element;
  autofocusValidate?: boolean;
}

export const SimpleDialog = (props: Props) => {
  const i18n = useI18n();
  let dialogRef!: HTMLDialogElement;
  let validateBtnRef!: HTMLButtonElement;
  let closeInitiatedInternally = false;

  const closeDialog = () => {
    closeInitiatedInternally = true;
    dialogRef.close();
  };

  const validateAndClose = () => {
    const result = props.onValidate?.();
    if (result === false) return; // caller signalled validation failure
    closeInitiatedInternally = true;
    dialogRef.close();
  };

  createEffect(() => {
    if (props.isOpen) {
      dialogRef.showModal();
      if (props.autofocusValidate && validateBtnRef) {
        validateBtnRef.focus();
      }
    } else {
      dialogRef.close();
    }
  });

  const handleTransitionEnd = () => {
    if (!dialogRef.open) {
      if (closeInitiatedInternally) {
        closeInitiatedInternally = false;
        props.onClose();
      }
      // @ts-ignore
      document.activeElement?.blur();
      document.body.focus();
    }
  };

  const header = (
    <div class="flex items-center justify-between">
      <h3 class="text-lg leading-6 font-medium">{props.title}</h3>
      <Show when={!props.buttonCancel}>
        <button
          class="ui-btn ui-btn-soft ui-btn-circle ui-btn-sm"
          type="button"
          title={i18n.t("Close")}
          onClick={closeDialog}
        >
          <VsChromeClose size={16} />
        </button>
      </Show>
    </div>
  );

  const buttons = (
    <Show when={props.buttonCancel || props.buttonValidate}>
      <div class="ui-modal-action">
        <Show when={props.buttonCancel}>
          <button type="button" class="ui-btn ui-btn-secondary" onClick={closeDialog}>
            {props.buttonCancel}
          </button>
        </Show>
        <Show when={props.buttonValidate}>
          <button ref={validateBtnRef} type="submit" class="ui-btn ui-btn-primary">
            {props.buttonValidate}
          </button>
        </Show>
      </div>
    </Show>
  );

  return (
    <dialog
      ref={dialogRef}
      class="ui-modal"
      onClose={() => {
        // Fired by backdrop/Escape - mark as internal close
        if (props.isOpen) closeInitiatedInternally = true;
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div class="ui-modal-box max-w-4xl">
        {header}
        {props.onValidate ? (
          <form
            method="dialog"
            onSubmit={(e) => {
              e.preventDefault();
              validateAndClose();
            }}
          >
            <div class="mt-2">{props.children}</div>
            {buttons}
          </form>
        ) : (
          <div class="mt-2">{props.children}</div>
        )}
      </div>
      <form method="dialog" class="ui-modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );
};
